import { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import i18n from '../i18n';
import AppLayout from '../components/layout/AppLayout';
import LoginPage from '../pages/LoginPage';
import DashboardPage from '../pages/DashboardPage';
import RichiestePage from '../pages/RichiestePage';
import DettaglioRichiestaPage from '../pages/DettaglioRichiestaPage';
import CreaRichiestaPage from '../pages/CreaRichiestaPage';
import OrdiniPage from '../pages/OrdiniPage';
import ProduttoriPage from '../pages/ProduttoriPage';
import DettaglioProduttorePage from '../pages/DettaglioProduttorePage';
import CreaProduttorePage from '../pages/CreaProduttorePage';
import TrasportatoriPage from '../pages/TrasportatoriPage';
import CreaTrasportatorePage from '../pages/CreaTrasportatorePage';
import DettaglioTrasportatorePage from '../pages/DettaglioTrasportatorePage';
import IntermediariPage from '../pages/IntermediariPage';
import CreaIntermediarioPage from '../pages/CreaIntermediarioPage';
import DettaglioIntermediarioPage from '../pages/DettaglioIntermediarioPage';
import ClientiPage from '../pages/ClientiPage';
import DettaglioClientePage from '../pages/DettaglioClientePage';
import CreaClientePage from '../pages/CreaClientePage';
import NotFoundPage from '../pages/NotFoundPage';
import UtentiPage from '../pages/UtentiPage';
import AccettaInvitoPage from '../pages/AccettaInvitoPage';
import PasswordDimenticataPage from '../pages/PasswordDimenticataPage';
import ReimpostaPasswordPage from '../pages/ReimpostaPasswordPage';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';
import RoleHomeRedirect from './RoleHomeRedirect';
import {
  ROLES, DESKTOP_ROLES, ANAGRAFICHE_E_ORDINI_VIEW_ROLES, ANAGRAFICHE_COMMERCIALI_WRITE_ROLES,
  ANAGRAFICHE_TECNICHE_WRITE_ROLES, RICHIESTE_VIEW_ROLES, RICHIESTE_WRITE_ROLES,
  FATTURAZIONE_ROLES, USCITE_ROLES,
} from '../lib/constants';
import ProgrammazionePage from '../pages/ProgrammazionePage';
import DettaglioConferimentoPage from '../pages/DettaglioConferimentoPage';
import AreePage from '../pages/AreePage';
import SettingsPage from '../pages/SettingsPage';
import DocumentiImpiantoPage from '../pages/DocumentiImpiantoPage';
import ScarichiPage from '../pages/ScarichiPage';
import ProduzioniPage from '../pages/ProduzioniPage';
import UscitePage from '../pages/UscitePage';
import NuovaUscitaPage from '../pages/NuovaUscitaPage';
import DettaglioUscitaPage from '../pages/DettaglioUscitaPage';
import SchedaBigBagPage from '../pages/SchedaBigBagPage';
import FatturazionePage from '../pages/FatturazionePage';

// Shell operatore (OP): lazy-loaded → chunk separato, non scaricato dai ruoli desktop.
import { OperatoreLayout, RegistraScaricoPage, RegistraProduzionePage } from './operatoreLazy';

function lazyEl(node: React.ReactNode) {
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen text-sm text-ink-soft">{i18n.t('common.azioni.caricamento')}</div>}>
      {node}
    </Suspense>
  );
}

// I set di ruoli delle rotte vivono in lib/constants.ts: le stesse regole
// governano i gate cosmetici nelle pagine, e tenerle in due posti le faceva
// divergere in silenzio.
export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <RoleHomeRedirect />
      </ProtectedRoute>
    ),
  },
  {
    path: '/login',
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  {
    path: '/accetta-invito',
    element: <AccettaInvitoPage />,
  },
  {
    path: '/password-dimenticata',
    element: <PasswordDimenticataPage />,
  },
  {
    path: '/reimposta-password',
    element: <ReimpostaPasswordPage />,
  },
  {
    path: '/op',
    element: (
      <ProtectedRoute allowedRoles={[ROLES.OP]}>
        {lazyEl(<OperatoreLayout />)}
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/op/scarico" replace /> },
      { path: 'scarico', element: lazyEl(<RegistraScaricoPage />) },
      { path: 'produzione', element: lazyEl(<RegistraProduzionePage />) },
    ],
  },
  {
    path: '/',
    element: (
      <ProtectedRoute allowedRoles={DESKTOP_ROLES}>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      {
        path: 'richieste',
        element: (
          <ProtectedRoute allowedRoles={RICHIESTE_VIEW_ROLES}>
            <RichiestePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'richieste/nuova',
        element: (
          <ProtectedRoute allowedRoles={RICHIESTE_WRITE_ROLES}>
            <CreaRichiestaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'richieste/:id',
        element: (
          <ProtectedRoute allowedRoles={RICHIESTE_VIEW_ROLES}>
            <DettaglioRichiestaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'ordini',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_E_ORDINI_VIEW_ROLES}>
            <OrdiniPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'ordini/:id',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_E_ORDINI_VIEW_ROLES}>
            <DettaglioRichiestaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'produttori',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_E_ORDINI_VIEW_ROLES}>
            <ProduttoriPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'produttori/nuovo',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_COMMERCIALI_WRITE_ROLES}>
            <CreaProduttorePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'produttori/:id',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_E_ORDINI_VIEW_ROLES}>
            <DettaglioProduttorePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'trasportatori',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_E_ORDINI_VIEW_ROLES}>
            <TrasportatoriPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'trasportatori/nuovo',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_TECNICHE_WRITE_ROLES}>
            <CreaTrasportatorePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'trasportatori/:id',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_E_ORDINI_VIEW_ROLES}>
            <DettaglioTrasportatorePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'intermediari',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_E_ORDINI_VIEW_ROLES}>
            <IntermediariPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'intermediari/nuovo',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_TECNICHE_WRITE_ROLES}>
            <CreaIntermediarioPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'intermediari/:id',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_E_ORDINI_VIEW_ROLES}>
            <DettaglioIntermediarioPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'clienti',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_E_ORDINI_VIEW_ROLES}>
            <ClientiPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'clienti/nuovo',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_COMMERCIALI_WRITE_ROLES}>
            <CreaClientePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'clienti/:id',
        element: (
          <ProtectedRoute allowedRoles={ANAGRAFICHE_E_ORDINI_VIEW_ROLES}>
            <DettaglioClientePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'utenti',
        element: (
          <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
            <UtentiPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'programmazione',
        element: <ProgrammazionePage />,
      },
      {
        path: 'conferimenti/:id',
        element: <DettaglioConferimentoPage />,
      },
      {
        path: 'scarichi',
        element: <ScarichiPage />,
      },
      {
        path: 'produzioni',
        element: <ProduzioniPage />,
      },
      {
        path: 'uscite',
        element: (
          <ProtectedRoute allowedRoles={USCITE_ROLES}>
            <UscitePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'uscite/nuova',
        element: (
          <ProtectedRoute allowedRoles={USCITE_ROLES}>
            <NuovaUscitaPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'uscite/:id',
        element: (
          <ProtectedRoute allowedRoles={USCITE_ROLES}>
            <DettaglioUscitaPage />
          </ProtectedRoute>
        ),
      },
      {
        // Destinazione del QR stampato sulle etichette dei big bag. Ruoli
        // desktop: e' una scheda di sola lettura, non un gate operativo.
        path: 'big-bags/:qrToken',
        element: (
          <ProtectedRoute allowedRoles={DESKTOP_ROLES}>
            <SchedaBigBagPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'fatturazione',
        element: (
          <ProtectedRoute allowedRoles={FATTURAZIONE_ROLES}>
            <FatturazionePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'aree',
        element: (
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.AMM, ROLES.COMMERCIALE, ROLES.DIR, ROLES.RT]}>
            <AreePage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'documenti-impianto',
        element: (
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.AMM, ROLES.COMMERCIALE, ROLES.RT, ROLES.DIR]}>
            <DocumentiImpiantoPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.AMM, ROLES.COMMERCIALE, ROLES.DIR, ROLES.RT]}>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
